import { ChevronLeft, ChevronRight } from "lucide-react";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useGetV1Plants } from "@solarkey/api-client";

interface SiteSwitcherProps {
  currentSite: string;
  tenant: string;
  scope: string;
}

export function SiteSwitcher({ currentSite, scope, tenant: _tenant }: SiteSwitcherProps) {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const query = useGetV1Plants({ period: "today", scope }, { query: { staleTime: 5 * 60_000 } });
  const items = query.data?.status === 200 ? (query.data.data.data.items ?? []) : [];
  const sites = items.map((it) => ({ id: it.site_id ?? "", name: it.site_id ?? "" }));

  if (sites.length < 2) return null;

  const idx = sites.findIndex((s) => s.id === currentSite);
  if (idx < 0) return null;

  const prev = idx > 0 ? sites[idx - 1] : undefined;
  const next = idx < sites.length - 1 ? sites[idx + 1] : undefined;
  const current = sites[idx];

  const goTo = (siteId: string) => {
    const newPath = pathname.replace(`/plants/${currentSite}/`, `/plants/${siteId}/`);
    void navigate({ to: newPath as never });
  };

  return (
    <div className="right">
      <span
        className="prev"
        role="button"
        tabIndex={0}
        aria-label="Previous site"
        aria-disabled={!prev}
        onClick={() => prev && goTo(prev.id)}
        onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && prev) goTo(prev.id); }}
        style={!prev ? { opacity: 0.35, cursor: "default" } : undefined}
      >
        <ChevronLeft size={14} />
      </span>
      <span className="pos">
        <strong>{current?.name ?? currentSite}</strong> · {idx + 1} of {sites.length}
      </span>
      <span
        className="next"
        role="button"
        tabIndex={0}
        aria-label="Next site"
        aria-disabled={!next}
        onClick={() => next && goTo(next.id)}
        onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && next) goTo(next.id); }}
        style={!next ? { opacity: 0.35, cursor: "default" } : undefined}
      >
        <ChevronRight size={14} />
      </span>
    </div>
  );
}
